import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { OrdenCompra } from '../types/ordenCompra.types';
import { OrdenCompraService } from '../services/ordenCompraService';

interface OrdenCompraState {
  items: OrdenCompra[];
  selectedItem: OrdenCompra | null;
  loading: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  fetchById: (id: number) => Promise<void>;
  save: (data: OrdenCompra) => Promise<void>;
  remove: (id: number) => Promise<void>;
  setSelectedItem: (item: OrdenCompra | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  items: [],
  selectedItem: null,
  loading: false,
  error: null,
};

export const useOrdenCompraStore = create<OrdenCompraState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchAll: async () => {
        set({ loading: true, error: null }, false, 'oc/fetchAll/start');
        try {
          const items = await OrdenCompraService.getAll();
          set({ items, loading: false }, false, 'oc/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar ordenes' }, false, 'oc/fetchAll/error');
        }
      },

      fetchById: async (id: number) => {
        set({ loading: true, error: null }, false, 'oc/fetchById/start');
        try {
          const item = await OrdenCompraService.getById(id);
          set({ selectedItem: item, loading: false }, false, 'oc/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar orden' }, false, 'oc/fetchById/error');
        }
      },

      save: async (data: OrdenCompra) => {
        set({ loading: true, error: null }, false, 'oc/save/start');
        try {
          await OrdenCompraService.save(data);
          set({ loading: false }, false, 'oc/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar' }, false, 'oc/save/error');
          throw error;
        }
      },

      remove: async (id: number) => {
        set({ loading: true, error: null }, false, 'oc/delete/start');
        try {
          await OrdenCompraService.delete(id);
          set((state) => ({
            items: state.items.filter((i) => i.idOrdenCompra !== id),
            loading: false,
          }), false, 'oc/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar' }, false, 'oc/delete/error');
        }
      },

      setSelectedItem: (item) => set({ selectedItem: item }, false, 'oc/setSelected'),
      clearError: () => set({ error: null }, false, 'oc/clearError'),
      reset: () => set(initialState, false, 'oc/reset'),
    }),
    { name: 'orden-compra-store' }
  )
);
